import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	Product,
	ErrorType,
	Severity,
	Team,
	createProductSpecificAnalyticsIntegrationPayload,
} from '@atlassian/growth-commons-error-analytics';

import { useSessionData, ConfluenceEdition } from '@confluence/session-data';

import { useGetTenantDataFromCGraphQL } from './useGetTenantDataFromCGraphQL';
import { BillingSourceSystem } from './__types__/useGetTenantDataFromCGraphQLQuery';

interface UseIsEligibleForRtSuppressionExperimentProps {
	experimentId: string;
	extraAnalyticsAttributes?: object;
	skip?: boolean;
}

interface UseIsEligibleForRtSuppressionExperimentReturn {
	isEligible: boolean;
	loading: boolean;
	error?: string | null | undefined;
	daysLeftInTrial?: number;
	isInTrialOrPreDunning?: boolean;
	analyticsAttributes?: {
		billingSourceSystem: string | null | undefined;
		predunningEndTime: number | null | undefined;
		trialEndDate: string | null | undefined;
		isInTrialOrPreDunning: boolean | undefined;
	};
}

/**
 * This hook orchestrates a multi-faceted eligibility check by combining session data,
 * fetching operations for users who don't meet the initial criteria, optimizing performance.
 *
 * Premium users in a trial or pre-dunning state.
 */
export const useIsEligibleForRtSuppression = ({
	experimentId,
	skip = false,
	extraAnalyticsAttributes = {},
}: UseIsEligibleForRtSuppressionExperimentProps): UseIsEligibleForRtSuppressionExperimentReturn => {
	const { edition, isLoggedIn } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isPremium = edition && edition === ConfluenceEdition.PREMIUM;
	const isEligibleForDataFetching = isPremium && isLoggedIn;

	const {
		billingSourceSystem,
		isInTrialOrPreDunning,
		predunningEndTime,
		trialEndDate,
		daysLeftInTrial,
		loading: isGetTenantDataFromCGraphQLLoading,
		error: isGetTenantDataFromCGraphQLError,
	} = useGetTenantDataFromCGraphQL({ skip: !isEligibleForDataFetching || skip });

	const isBillingSourceSystemCCP = billingSourceSystem === BillingSourceSystem.CCP;

	if (isGetTenantDataFromCGraphQLError) {
		const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
			product: Product.CONFLUENCE,
			team: Team.GROWTH_LUNA,
			experimentMeta: {
				experimentKey: experimentId,
			},
			errorType: ErrorType.GRAPHQL,
			errorMessage: isGetTenantDataFromCGraphQLError?.message,
			severity: Severity.HIGH,
			source: `useGetTenantDataFromCGraphQL hook`,
			additionalAttributes: extraAnalyticsAttributes,
		});

		createAnalyticsEvent(eventPayload).fire();

		return {
			isEligible: false,
			loading: false,
			error: isGetTenantDataFromCGraphQLError?.message,
		};
	}

	/**
	 * Final eligibility determination based on all criteria.
	 *
	 * Premium users in a trial or pre-dunning state.
	 */
	const isEligibleForExperiment =
		isBillingSourceSystemCCP && !!isPremium && isLoggedIn && isInTrialOrPreDunning;

	return {
		isEligible: isEligibleForExperiment,
		loading: isGetTenantDataFromCGraphQLLoading,
		error: null,
		daysLeftInTrial,
		isInTrialOrPreDunning,
		analyticsAttributes: {
			billingSourceSystem,
			predunningEndTime,
			trialEndDate,
			isInTrialOrPreDunning,
		},
	};
};
